import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, History as HistoryIcon, Loader2, Mail, MessageSquare, AlertTriangle, ClipboardList, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth, canWrite } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DeleteRecordButton } from "@/components/DeleteRecordButton";
import { consultationDeleteEligibility } from "@/lib/deleteEligibility";
import { formatNotes, hasVisibleNotes } from "@/lib/safeDisplay";
import { ReasonModal } from "@/components/ReasonModal";
import { CANCEL_REQUEST_REASONS } from "@/lib/lists";
import { getLifecycleStatus, lifecycleBadgeVariant } from "@/lib/withdrawGate";

const STATUS_FALLBACK = [
  "New Request", "Awaiting Information", "Scheduled", "In Review", "Declined", "Closed",
];

function fmtDate(iso) {
  if (!iso) return "\u2014";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function Read({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`text-[13px] ${mono ? "font-mono" : ""}`} data-testid={`consultation-read-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        {value || <span className="text-muted-foreground italic">\u2014</span>}
      </div>
    </div>
  );
}

export default function ConsultationRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const writer = canWrite(user);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState(STATUS_FALLBACK);
  const [history, setHistory] = useState([]);
  const [intakeWorkspaces, setIntakeWorkspaces] = useState([]);
  const [creatingIntake, setCreatingIntake] = useState(false);

  const [form, setForm] = useState({
    status: "",
    internal_notes: "",
    follow_up_owner: "",
    outcome: "",
  });

  function setField(k, v) { setForm((s) => ({ ...s, [k]: v })); }

  // Phase A Run 2 — cancel modal
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  async function submitCancel({ category, detail }) {
    setCancelBusy(true);
    try {
      await api.post(`/kandake/consultations/${id}/cancel`, { category, detail });
      toast.success("Consultation cancelled. Audit record created.");
      setCancelOpen(false);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCancelBusy(false);
    }
  }
  const lifecycleStatus = getLifecycleStatus(doc);
  const isCancelled = lifecycleStatus === "CANCELLED";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/kandake/consultations/${id}`);
      setDoc(r.data);
      setForm({
        status: r.data.status || "",
        internal_notes: r.data.internal_notes || "",
        follow_up_owner: r.data.follow_up_owner || "",
        outcome: r.data.outcome || "",
      });
      const audit = await api.get("/audit", { params: { record_type: "kandake_consultation", record_id: id, limit: 50 } });
      setHistory(Array.isArray(audit.data) ? audit.data : []);
      // MP12 Module 2B — related intake workspaces (read-only listing).
      try {
        const iw = await api.get("/intake-workspaces", { params: { linked_consultation_id: id } });
        setIntakeWorkspaces(Array.isArray(iw.data) ? iw.data : []);
      } catch { setIntakeWorkspaces([]); }
    } catch (e) {
      toast.error(errMsg(e, "Could not load consultation."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!writer) return;
    load();
    api.get("/kandake/meta").then((r) => {
      if (Array.isArray(r.data?.statuses)) setStatuses(r.data.statuses);
    }).catch(() => {});
  }, [writer, load]);

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/kandake/consultations/${id}`, form);
      setDoc(r.data);
      toast.success("Consultation updated.");
      const audit = await api.get("/audit", { params: { record_type: "kandake_consultation", record_id: id, limit: 50 } });
      setHistory(Array.isArray(audit.data) ? audit.data : []);
    } catch (e) {
      toast.error(errMsg(e, "Could not save changes."));
    } finally {
      setSaving(false);
    }
  }

  async function createIntakeFromConsultation() {
    // MP12 Module 2B — one-click way to start an Internal Intake Workspace
    // pre-linked to this consultation. Everything captured in the new workspace
    // remains provisional until the operator explicitly routes it.
    if (creatingIntake) return;
    setCreatingIntake(true);
    try {
      const payload = {
        intake_source: "internal_referral",
        client_name: doc?.full_name || "",
        linked_consultation_id: id,
      };
      const r = await api.post("/intake-workspaces", payload);
      toast.success("Draft intake created.");
      navigate(`/intake-workspaces/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e, "Could not create intake workspace."));
    } finally {
      setCreatingIntake(false);
    }
  }

  if (!writer) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="consultation-readonly">
        <Alert>
          <AlertDescription>
            Consultation records are restricted to Catalog Manager, GFMP Reviewer, and Super Admin.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading || !doc) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8" data-testid="consultation-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading\u2026
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="consultation-detail-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/consultation-requests")} data-testid="consultation-back">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> All consultation requests
        </Button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
            Consultation · <span className="font-mono normal-case">{doc.id?.slice(0, 8)}</span>
          </div>
          <h1 className="font-display text-2xl md:text-3xl tracking-tight">
            {doc.full_name || "(no contact name)"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            <Mail className="w-3 h-3 inline mr-1.5" />
            <a href={`mailto:${doc.email}`} className="underline" data-testid="consultation-mailto">{doc.email}</a>
            {doc.org_name ? ` · ${doc.org_name}` : ""}
            {doc.phone ? ` · ${doc.phone}` : ""}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide" data-testid="consultation-current-status">
            {doc.status}
          </Badge>
          {isCancelled && (
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${lifecycleBadgeVariant(lifecycleStatus).className}`} data-testid="consultation-lifecycle-badge">
              {lifecycleBadgeVariant(lifecycleStatus).label}
            </Badge>
          )}
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Received {fmtDate(doc.created_at)}
          </span>
          {writer && !isCancelled && (
            <Button variant="outline" size="sm" onClick={() => setCancelOpen(true)} data-testid="consultation-cancel-button" style={{borderColor:"hsl(0 70% 42%)", color:"hsl(0 70% 42%)"}}>
              Cancel Request
            </Button>
          )}
        </div>
      </div>

      {doc.email_delivery === "pending" && (
        <Alert data-testid="consultation-email-pending">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription className="text-[12.5px]">
            Outbound email delivery is not yet wired — this submission lives in the database only.
            Acknowledgements and follow-ups must be sent manually for now.
          </AlertDescription>
        </Alert>
      )}

      {/* Public-form payload (read-only) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Submitted details
          </CardTitle>
          <CardDescription>Verbatim payload submitted through the public Kandake form.</CardDescription>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <Read label="Full name" value={doc.full_name} />
          <Read label="Organisation" value={doc.org_name} />
          <Read label="Email" value={doc.email} />
          <Read label="Phone" value={doc.phone} />
          <Read label="Catalog size" value={doc.catalog_size} />
          <Read label="Primary need" value={doc.primary_need} />
          <div className="sm:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Description</div>
            <div className="text-[13px] whitespace-pre-wrap" data-testid="consultation-read-description">
              {doc.description || <span className="text-muted-foreground italic">\u2014</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Internal triage */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Internal triage</CardTitle>
          <CardDescription>
            Update status, follow-up owner, internal notes, and outcome. These fields are internal
            only and never appear on the public Kandake Compliance Services site.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger data-testid="consultation-status-select">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="follow_up_owner">Follow-up owner</Label>
              <Input id="follow_up_owner" value={form.follow_up_owner} onChange={(e) => setField("follow_up_owner", e.target.value)}
                     maxLength={160} placeholder="e.g. operator name / email"
                     data-testid="consultation-followup-owner" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="outcome">Outcome</Label>
              <Input id="outcome" value={form.outcome} onChange={(e) => setField("outcome", e.target.value)}
                     maxLength={400} placeholder="Short-form outcome (handed off, engaged, declined, etc.)"
                     data-testid="consultation-outcome" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="internal_notes">Internal notes</Label>
              <Textarea id="internal_notes" rows={4} value={form.internal_notes} onChange={(e) => setField("internal_notes", e.target.value)}
                        maxLength={4000} data-testid="consultation-internal-notes" />
            </div>
          </div>

          <Separator />

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={load} disabled={saving} data-testid="consultation-revert">
              Revert
            </Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="consultation-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              Save changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* MP12 Module 2B — Related Internal Intake Workspaces */}
      <Card data-testid="consultation-related-intakes">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> Related Intake Workspaces
          </CardTitle>
          <CardDescription>
            Internal, structured intake sessions linked to this consultation. Everything captured on an intake is <strong>provisional</strong> until the operator explicitly routes it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {intakeWorkspaces.length === 0 ? (
            <div className="text-[12.5px] text-muted-foreground italic" data-testid="consultation-intakes-empty">
              No intake workspaces are linked to this consultation yet.
            </div>
          ) : (
            <ul className="divide-y border rounded-md" data-testid="consultation-intakes-list">
              {intakeWorkspaces.map((w) => (
                <li key={w.id} className="p-2.5 flex items-center gap-3" data-testid={`consultation-intake-row-${w.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-medium truncate">
                      {w.client_name || "(no client name)"}
                      {w.asset_title ? <span className="text-muted-foreground"> \u00b7 {w.asset_title}</span> : null}
                    </div>
                    <div className="text-[10.5px] text-muted-foreground">
                      <span className="font-mono">{w.id.slice(0, 8)}</span> \u00b7 {w.intake_source} \u00b7 updated {fmtDate(w.updated_at)}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wide" data-testid={`consultation-intake-status-${w.id}`}>
                    {w.status}
                  </Badge>
                  <Button asChild size="sm" variant="ghost" data-testid={`consultation-intake-open-${w.id}`}>
                    <Link to={`/intake-workspaces/${w.id}`}>Open</Link>
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              onClick={createIntakeFromConsultation}
              disabled={creatingIntake}
              data-testid="consultation-new-intake"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              {creatingIntake ? "Creating\u2026" : "New intake linked to this consultation"}
            </Button>
            <Button asChild size="sm" variant="ghost" data-testid="consultation-all-intakes">
              <Link to="/consultation-requests?tab=intake">Open Intake Workspace list</Link>
            </Button>
          </div>
        </CardContent>
      </Card>


      {/* Audit history */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <HistoryIcon className="w-4 h-4" /> Audit history
          </CardTitle>
          <CardDescription>All inquiry lifecycle changes recorded against this record.</CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-[12.5px] text-muted-foreground italic">No audit entries yet.</div>
          ) : (
            <ol className="space-y-2" data-testid="consultation-history-list">
              {history.map((h) => (
                <li key={h.audit_id} className="rounded-md border p-2.5 text-[12px]">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">{h.action_type}</span>
                    <span className="text-muted-foreground font-mono">{fmtDate(h.timestamp)}</span>
                  </div>
                  <div className="text-muted-foreground mt-1">
                    by <span className="text-foreground">{h.user_name || h.user_email || "\u2014"}</span>
                    {hasVisibleNotes(h.notes) && <span> · {formatNotes(h.notes, { fallback: "" })}</span>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      {/* MP11 Phase 2 — Danger zone: whole-record delete for a Consultation.
          Super Admin only (public source has no logged-in creator). */}
      {doc?.id && (() => {
        const elig = consultationDeleteEligibility(doc, user);
        const shortId = (doc.id || "").slice(0, 8).toUpperCase();
        return (
          <DeleteRecordButton
            recordType="consultation"
            recordLabel="Consultation"
            endpoint={`/kandake/consultations/${doc.id}`}
            confirmToken={shortId}
            confirmTokenLabel="short id"
            disabled={!elig.allowed}
            disabledReason={elig.reason}
            onDeleted={() => { navigate("/consultation-requests"); }}
            testidPrefix="consultation-delete"
            isSuperAdmin={user?.role === "super_admin"}
          />
        );
      })()}

      <ReasonModal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel Consultation Request"
        description="Soft-cancels this request. Historical record, notes, files, and audit history are preserved. Reason is required."
        categoryOptions={CANCEL_REQUEST_REASONS}
        confirmLabel="Cancel Request"
        severity="high"
        busy={cancelBusy}
        testid="cancel-consultation-modal"
        onConfirm={submitCancel}
      />
    </div>
  );
}
